/* eslint-disable max-lines */
import styled from 'styled-components';
import _ from 'lodash';
import { CircularProgress, Link } from '@material-ui/core';
import Autocomplete from 'components/base/Autocomplete';
import { Flex, Padding } from 'components/base/layout-components';
import Text from 'components/base/Text';
import InfoBox from 'components/InfoBox';
import { SPACES, SIZES } from 'common/constants/sizes';
import * as LighthouseService from 'common/services/lighthouse.service';
import { pluralize } from 'common/utils/utils';
import * as TrackingService from 'common/services/tracking.service';
import { TRACKING } from 'common/constants/constants';
import StatefulInfoBox from 'components/StatefulInfoBox';
import { useSelector } from 'react-redux';
import { isPmToolAzureBoardsSelector } from 'state/selectors/sensors-authorizations';

const CircularProgressWrapper = styled.div`
  width: 30rem;
  margin-top: 0.7rem;
`;

export const IssueTypesSection = ({
  issueTypes,
  selectedIssueTypes,
  setSelectedIssueTypes,
  onConfigChanged
}) => {
  return (
    <Flex direction='column'>
      <Text size='medium' color='shuttleGray' mb='1rem'>
        By issue type:
      </Text>
      {!issueTypes.length ? (
        <CircularProgressWrapper>
          <CircularProgress size={20} />
        </CircularProgressWrapper>
      ) : (
        <Autocomplete
          width='30rem'
          multiple
          disableClearable
          placeholder='All issue types'
          options={issueTypes.map(({ name, iconUrl: icon }) => {
            return {
              value: name,
              displayValue: (
                <Flex items='center'>
                  {icon && <img src={icon} width={14} height={14} alt={name} />}
                  <Text color='shark' pl='0.5rem' size='medium'>
                    {name}
                  </Text>
                </Flex>
              ),
              searchableValue: name
            };
          })}
          selectedOptionValue={selectedIssueTypes}
          onChange={values => {
            onConfigChanged(setSelectedIssueTypes)(values);
            TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
              filter_name: TRACKING.FILTER.ISSUE_TYPE,
              page: TRACKING.PAGES.SETTINGS,
              page_tab: TRACKING.PAGE_TABS.MTTR
            });
          }}
        />
      )}
    </Flex>
  );
};

export const PredictedBugSection = ({
  predictionCount,
  selectedIssueTypes,
  selectedLabels,
  selectedCustomFields,
  selectedCustomFieldsValues,
  customFields,
  releasesCount = null,
  hasReleases = false
}) => {
  return (
    <Flex direction='column' items='flex-start'>
      <InfoBox iconAlign='center'>
        <Padding l='x-x-small'>
          <Text size='medium' color='shuttleGray'>
            <b>
              {predictionCount}{' '}
              {hasReleases ? 'Incidents ' : 'Issues completed '}
            </b>
            {hasReleases && (
              <>
                and <b>{releasesCount} Deployments </b>
              </>
            )}
            were found in the last 30 days
            {releasesCount && predictionCount >= releasesCount && (
              <>
                ; these numbers seem too high and will result in 100% CFR - try
                readjusting your settings
              </>
            )}
          </Text>
          <Flex items='center'>
            <Text size='medium' color='shuttleGray'>
              filtered by:
            </Text>
            <Text size='medium' color='shuttleGray' pl={SIZES['tiny']}>
              {pluralize(
                (selectedIssueTypes || []).length,
                'issue type',
                undefined,
                false
              )}
              :
            </Text>
            <Text
              size='medium'
              color='shuttleGray'
              weight='bold'
              pl={SIZES['tiny']}
            >
              {(selectedIssueTypes || []).length > 0
                ? selectedIssueTypes.join(', ')
                : 'All'}
            </Text>
            <Text size='medium' color='shuttleGray' pl={SIZES['tiny']}>
              and{' '}
              {pluralize(
                (selectedLabels || []).length,
                'label',
                undefined,
                false
              )}
              :
            </Text>
            <Text
              size='medium'
              color='shuttleGray'
              weight='bold'
              pl={SIZES['tiny']}
            >
              {(selectedLabels || []).length > 0
                ? selectedLabels.join(', ')
                : 'All'}
            </Text>
            {customFields && (
              <>
                <Text size='medium' color='shuttleGray' pl={SIZES['tiny']}>
                  and{' '}
                  {selectedCustomFields
                    ? (
                        customFields.find(
                          ({ id }) => id === selectedCustomFields
                        ) || {}
                      ).name || 'fields'
                    : 'fields'}
                  :
                </Text>
                <Text
                  size='medium'
                  color='shuttleGray'
                  weight='bold'
                  pl={SIZES['tiny']}
                >
                  {(selectedCustomFieldsValues || []).length > 0
                    ? selectedCustomFieldsValues.join(', ')
                    : 'All'}
                </Text>
              </>
            )}
          </Flex>
        </Padding>
      </InfoBox>
    </Flex>
  );
};

export const PmMetricsHeader = ({
  history,
  saveSettings,
  title,
  text,
  articleId,
  isSaveEnabled = true,
  isOrgSettings = false
}) => {
  return (
    <Flex direction='row' justify='space-between' items='center' pt='medium'>
      <Flex justify='flex-start' direction='column'>
        <Text
          size='large'
          weight='500'
          textTransform='uppercase'
          pb={SPACES.small}
        >
          {title}
        </Text>
        <Flex items='center' pb='x-small'>
          <Text size='medium' color='shuttleGray'>
            {text}
          </Text>
          <Text size='medium' pl={SPACES['x-small']}>
            <Link onClick={() => LighthouseService.navigateArticle(articleId)}>
              learn more
            </Link>
          </Text>
        </Flex>
        {
          <Flex items='center' pb='x-small'>
            <Text size='medium' color='shuttleGray'>
              View {title} on
            </Text>
            <Text size='medium' pl={SPACES['x-small']}>
              <Link
                onClick={() =>
                  history.push({
                    pathname: '/performance/pm'
                  })
                }
              >
                Project Management Dashboard
              </Link>
            </Text>
          </Flex>
        }
      </Flex>
    </Flex>
  );
};

export const PredictCfrIssuesCount = ({
  issuesCount,
  releasesCount,
  selectedFilters
}) => {
  const isPmToolAzureBoards = useSelector(isPmToolAzureBoardsSelector);
  const isEmptySelectedFilters = _.values(selectedFilters).every(_.isEmpty);
  const {
    loading,
    error,
    total: totalIncidents,
    sinceDaysCounter
  } = issuesCount;
  const { total: totalDeployments } = releasesCount;

  const isHighestCFR =
    totalDeployments > 0 && totalIncidents >= totalDeployments;

  const sinceWhenString =
    sinceDaysCounter > 0 ? `in the last ${sinceDaysCounter} days` : '';

  return (
    <StatefulInfoBox
      isError={error}
      isLoading={loading}
      isHidden={isEmptySelectedFilters || isPmToolAzureBoards}
    >
      <Flex direction='column'>
        <Text>
          Based on these filters - we found <b>{totalIncidents} Incidents</b>{' '}
          and <b>{totalDeployments} Deployments</b> {sinceWhenString}
        </Text>
        {isHighestCFR && (
          <Text color='shuttleGray'>
            these numbers seem too high and will result in 100% CFR - try
            readjusting your settings
          </Text>
        )}
      </Flex>
    </StatefulInfoBox>
  );
};
